import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createMenulyticsReport,
  getMenulyticsReports,
  MenulyticsReport,
  queryKeys,
  retryMenulyticsReport,
  SuccessToast,
  useBusyAction,
} from '@vgrubs/components';
import { useCallback, useState } from 'react';

/**
 * Puts a report into a cached list: replaced in place when the id is already
 * there (a repeated Generate hands back the same report), on top otherwise.
 */
export const upsertReport = (
  list: MenulyticsReport[] | undefined,
  report: MenulyticsReport
) => {
  const rows = list ?? [];
  return rows.some((row) => row.id === report.id)
    ? rows.map((row) => (row.id === report.id ? report : row))
    : [report, ...rows];
};

/**
 * A location's Menu Health reports, plus Generate and Retry. Rows only ever
 * change through `patch`, which the socket, the share modal and the two
 * actions below all go through.
 */
export const useMenulyticsReports = (locationId: string, enabled: boolean) => {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.menulytics.reports(locationId);

  const reports = useQuery({
    queryKey,
    queryFn: async () => {
      const { data } = await getMenulyticsReports(locationId);
      return data.data;
    },
    enabled,
    // Opening the modal again shows the list as the backend has it now: other
    // admins' reports never reach this socket.
    staleTime: 0,
  });

  const patch = useCallback(
    (report: MenulyticsReport) =>
      queryClient.setQueryData<MenulyticsReport[]>(
        queryKeys.menulytics.reports(locationId),
        (list) => upsertReport(list, report)
      ),
    [queryClient, locationId]
  );

  const generateAction = useBusyAction();
  const retryAction = useBusyAction();
  const [retryingId, setRetryingId] = useState<number | null>(null);

  const generate = (brandId: number) =>
    generateAction.run(async () => {
      const { data } = await createMenulyticsReport(locationId, { brandId });
      patch(data.data);
      if (data.data.status === 'completed') {
        SuccessToast(
          'Nothing changed since the last report, so it is ready already.'
        );
      }
    });

  const retry = async (reportId: number) => {
    if (retryAction.busy) return;
    setRetryingId(reportId);
    await retryAction.run(async () => {
      const { data } = await retryMenulyticsReport(locationId, reportId);
      patch(data.data);
    });
    setRetryingId(null);
  };

  return {
    reports: reports.data ?? [],
    isLoading: reports.isLoading,
    patch,
    refetch: reports.refetch,
    generate,
    generating: generateAction.busy,
    retry,
    /** The row whose Retry is in flight. */
    retryingId: retryAction.busy ? retryingId : null,
  };
};
